// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
import React, {createContext, useState, ReactNode, useEffect} from 'react';
import {
  doc,
  getFirestore,
  setDoc,
  type DocumentReference,
} from 'firebase/firestore';
import {alertError} from 'components/completion/Alerts';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {
  UserAssignmentProgressMap,
} from 'assignments/models/userAssignmentProgressMapInterfaces';
import { getUserAssignmentProgressMapDocumentReference } from 'assignments/models/userAssignmentProgressMapReferences';
import {
  getUserLessonsProgressMapRef,
  type UserLessonsProgressMap,
} from 'course/lessons/userLessonsProgressMap';
import {
  getSelfCheckProgressDocumentReference,
  type SelfCheckProgress,
} from 'assignments/models/selfCheckProgress';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useAuthUid} from 'hooks/user/useUserId';
import type {UserId} from 'types/common';
import type {TranslationFunction} from 'components/translation/utils';
import {
  mergeProgressMaps,
  useParentCourseId,
} from 'course/application/general/utils/parentCourseProgress';

/**
 * TODO: refactor to use data instead of predicate functions
 * (so that folks can react to changes)
 */

/*
 * Lessons, assignment, and section progress all have designated documents in firestore for each user to determine there progress.
 * For selfCheck necessary progresses, one idea is all self check, anselfCheck idea is, if they are coded in, give them an attribute in
 * some selfCheck user specific document to add a completed boolean. Not sure how to police this.
 */

interface CompletionContextType {
  isCompletionLoading: boolean;
  isLessonCompleted: (lessonId) => boolean;
  isLessonSlideCompleted: (lessonId, slideId) => boolean;
  isAssnCompleted: (assnId) => boolean;
  isSectionCompleted: (sectionAndWeekId) => boolean;
  isSelfCheckCompleted: (completedKey) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setLessonCompleted: (lessonId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setLessonSlideCompleted: (lessonId, slideId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setAssnCompleted: (assnId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setSectionCompleted: (sectionAndWeekId) => any;
  setSelfCheckCompleted: (
    completedKey: string,
    isSet?: boolean,
  ) => Promise<void>;
  // For TeachNow Training
  isTeachNowTrainingCompleted: () => boolean;
  isTeachNowTrainingSlideCompleted: (slideId) => boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setTeachNowTrainingSlideCompleted: (slideId) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setTeachNowTrainingCompleted: (a) => any;
  numTeacherPuzzlesCompleted: (puzzleType) => number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  updateTeacherPuzzlesCompleted: (puzzleType) => any;
  isReadAboutTeachNowCompleted: () => boolean;
  // if you need something responsive, listen to these
  lessonsProgress: UserLessonsProgressMap;
  assnProgress: UserAssignmentProgressMap;
  selfCheckProgress: SelfCheckProgress;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  sectionProgress: any;
  completionUpdateBit: boolean;
}

/**
 * Fallback shape for consumers with no ancestor {@link CompletionProvider} in
 * their tree, and reused as the base for lean, single-purpose completion
 * providers (e.g. {@link ApplicationLessonProgressProvider}) that only back a
 * few of these fields with real data and want the rest to behave like "no
 * progress anywhere" rather than throwing.
 */
export const defaultCompletion: CompletionContextType = {
  isCompletionLoading: true,
  isLessonCompleted: () => {
    return false;
  },
  isLessonSlideCompleted: () => {
    return false;
  },
  isAssnCompleted: () => {
    return false;
  },
  isSectionCompleted: () => {
    return false;
  },
  isSelfCheckCompleted: () => {
    return false;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setLessonCompleted: a => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setLessonSlideCompleted: (a, b) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setAssnCompleted: a => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  setSectionCompleted: a => {},
  setSelfCheckCompleted: async () => {},
  // For TeachNow Training
  isTeachNowTrainingCompleted: () => {
    return false;
  },
  isTeachNowTrainingSlideCompleted: () => {
    return false;
  },
  setTeachNowTrainingSlideCompleted: () => {},
  setTeachNowTrainingCompleted: () => {},
  numTeacherPuzzlesCompleted: () => {
    return 0;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  updateTeacherPuzzlesCompleted: a => {},
  isReadAboutTeachNowCompleted: () => {
    return false;
  },
  lessonsProgress: {},
  assnProgress: {},
  selfCheckProgress: {},
  sectionProgress: {},
  completionUpdateBit: false,
};

// Create the context with initial undefined value
export const CompletionContext = createContext<
  CompletionContextType | undefined
>(defaultCompletion);

interface CompletionProviderProps {
  children: ReactNode;
  courseId: string;
}

/**
 * Provides the signed-in user's lesson, assignment, section, self-check and
 * TeachNow progress for a course.
 *
 * @param props.courseId - The course whose progress to provide.
 * @param props.children - The subtree that reads the progress.
 * @return The provider. With no signed-in user it provides
 *   {@link defaultCompletion} and reads nothing.
 * @throws Never.
 */
export function CompletionProvider({
  courseId,
  children,
}: CompletionProviderProps) {
  const userId = useAuthUid();
  if (!userId) {
    return (
      <CompletionContext.Provider value={defaultCompletion}>
        {children}
      </CompletionContext.Provider>
    );
  }
  return (
    <UserCompletionProvider userId={userId} courseId={courseId}>
      {children}
    </UserCompletionProvider>
  );
}

const UserCompletionProvider = ({
  userId,
  courseId,
  children,
}: CompletionProviderProps & {userId: UserId}) => {
  const t = useTranslateFunction('miscUI');

  const db = getFirestore();

  // Document Refs:
  const usersPath = `/users/${userId}`;
  const trackablesPath = `${usersPath}/${courseId}`;
  const lessonsProgressDocRef = getUserLessonsProgressMapRef({
    userId,
    courseId,
  });
  const assnProgressDocRef = getUserAssignmentProgressMapDocumentReference({
    userId,
    courseId,
  });
  const sectionProgressDocRef = doc(db, `${trackablesPath}/sectionAttendance`);
  const selfCheckProgressDocRef = getSelfCheckProgressDocumentReference({
    userId,
    courseId,
  });
  const teachNowTrainingDocRef = doc(
    db,
    `${trackablesPath}/teachNowTrainingProgress`,
  );
  const teachNowTeacherPuzzlesDocRef = doc(
    db,
    `${trackablesPath}/teachNowTeacherPuzzlesProgress`,
  );
  // Application tasks are completed under the parent course (see
  // `ApplicationTaskProgressProvider`), so its lesson and assignment progress
  // is overlaid onto this course's own. Writes below still target this course.
  const {parentCourseId, isLoading: parentCourseLoading} =
    useParentCourseId(courseId);
  const parentAssnProgressDocRef = parentCourseId
    ? getUserAssignmentProgressMapDocumentReference({
        userId,
        courseId: parentCourseId,
      })
    : null;
  const parentLessonsProgressDocRef = parentCourseId
    ? getUserLessonsProgressMapRef({userId, courseId: parentCourseId})
    : null;

  // progress lookups (now with useDocumentData)
  const [ownLessonsProgress, lessonsLoading] = useDocumentData(
    lessonsProgressDocRef,
  );
  const [ownAssnProgress, assnLoading] = useDocumentData(assnProgressDocRef);
  const [sectionProgress, sectionLoading] = useDocumentData(
    sectionProgressDocRef,
  );
  const [selfCheckProgress, selfCheckLoading] = useDocumentData(
    selfCheckProgressDocRef,
  );
  const [completionUpdateBit, setCompletionUpdateBit] = useState(false);
  const [teachNowTrainingProgress, teachNowTrainingLoading] = useDocumentData(
    teachNowTrainingDocRef,
  );
  const [teachNowTeacherPuzzlesProgress, teachNowTeacherPuzzlesLoading] =
    useDocumentData(teachNowTeacherPuzzlesDocRef);
  const [parentAssnProgress, parentAssnLoading] = useDocumentData(
    parentAssnProgressDocRef,
  );
  const [parentLessonsProgress, parentLessonsLoading] = useDocumentData(
    parentLessonsProgressDocRef,
  );

  const lessonsProgress = mergeProgressMaps(
    ownLessonsProgress,
    parentLessonsProgress,
  );
  const assnProgress = mergeProgressMaps(ownAssnProgress, parentAssnProgress);

  // Question: will this load for students if they don't have the teachNowTrainingProgress docs?
  const isCompletionLoading =
    lessonsLoading ||
    assnLoading ||
    selfCheckLoading ||
    sectionLoading ||
    teachNowTrainingLoading ||
    teachNowTeacherPuzzlesLoading ||
    parentCourseLoading ||
    parentAssnLoading ||
    parentLessonsLoading;

  const isLessonCompleted = (lessonId: string) => {
    // the lesson is only considered completed if it has been
    // explicitly marked as completed, even if all slides are completed
    if (!lessonsProgress) {
      return undefined;
    }
    return Boolean(lessonsProgress[lessonId]);
  };

  const numTeacherPuzzlesCompleted = puzzleType => {
    if (!teachNowTeacherPuzzlesProgress) {
      return 0;
    }
    if (Object.keys(teachNowTeacherPuzzlesProgress).includes(puzzleType)) {
      if (teachNowTeacherPuzzlesProgress[puzzleType]) {
        const numCompleted = teachNowTeacherPuzzlesProgress[puzzleType];
        return numCompleted;
      }
    }
    return 0;
  };

  const updateTeacherPuzzlesCompleted = async puzzleType => {
    try {
      const numCompleted = numTeacherPuzzlesCompleted(puzzleType) + 1;
      setDoc(
        teachNowTeacherPuzzlesDocRef,
        {
          [puzzleType]: numCompleted,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    return false;
  };

  const isTeachNowTrainingCompleted = () => {
    if (!teachNowTrainingProgress) {
      return undefined;
    }
    return teachNowTrainingProgress.completed;
  };

  const isReadAboutTeachNowCompleted = () => {
    if (!teachNowTrainingProgress) {
      return undefined;
    }
    return teachNowTrainingProgress.readAboutTeachNow;
  };

  const isTeachNowTrainingSlideCompleted = slideId => {
    if (!teachNowTrainingProgress) {
      return undefined;
    }
    if (Object.keys(teachNowTrainingProgress).includes(slideId)) {
      if (teachNowTrainingProgress[slideId]) {
        return true;
      }
    }
    return false;
  };

  const setTeachNowTrainingSlideCompleted = async slideId => {
    try {
      setDoc(
        teachNowTrainingDocRef,
        {
          [slideId]: true,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    return false;
  };

  const setTeachNowTrainingCompleted = async () => {
    try {
      // This is so that the green checkmark appears in the roadmap when students complete the training
      setDoc(
        selfCheckProgressDocRef,
        {
          studentTeachNowTraining: true,
        },
        {merge: true},
      );
      setDoc(
        teachNowTrainingDocRef,
        {
          completed: true,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    return false;
  };

  const isLessonSlideCompleted = (lessonId, slideId) => {
    if (!lessonsProgress) {
      return undefined;
    }
    const lessonSlideKey = `${lessonId}/${slideId}`;
    if (!Object.keys(lessonsProgress).includes(lessonSlideKey)) {
      return false;
    }
    return lessonsProgress[lessonSlideKey];
  };

  const setLessonSlideCompleted = async (lessonId, slideId) => {
    // Without a slide id the key would read `{lessonId}/undefined`.
    if (!lessonId || !slideId) {
      return;
    }
    try {
      const lessonSlideKey = `${lessonId}/${slideId}`;
      setDoc(
        lessonsProgressDocRef,
        {
          [lessonSlideKey]: true,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    return false;
  };

  const isAssnCompleted = assnId => {
    if (!assnProgress) {
      return undefined;
    }
    return Boolean(assnProgress[assnId]);
  };

  const isSectionCompleted = sectionAndWeekId => {
    if (!sectionProgress) {
      return undefined;
    }

    if (!Object.keys(sectionProgress).includes(sectionAndWeekId.toString())) {
      return false;
    }

    return sectionProgress[sectionAndWeekId];
  };

  const isSelfCheckCompleted = selfCheckKey => {
    if (!selfCheckKey) {
      return false;
    }
    if (!selfCheckProgress) {
      return undefined;
    }
    if (!Object.keys(selfCheckProgress).includes(selfCheckKey)) {
      return false;
    }
    return selfCheckProgress[selfCheckKey];
  };

  const setLessonCompleted = async lessonId => {
    if (!lessonId) {
      return false;
    }

    try {
      setDoc(
        lessonsProgressDocRef,
        {
          [lessonId]: true,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    setCompletionUpdateBit(!completionUpdateBit);
  };

  const setAssnCompleted = async assnId => {
    // Guard the read: `assnProgress` is undefined until the doc loads, and is
    // absent entirely for a user who has never completed anything in this
    // course. Without `?.` this line throws before the write, so the
    // assignment never gets marked complete for those users.
    if (assnProgress?.[assnId]) {
      return;
    }

    try {
      setDoc(
        assnProgressDocRef,
        {
          [assnId]: true,
        },
        {merge: true},
      );
    } catch (e) {
      alertError(e, t);
    }
    setCompletionUpdateBit(!completionUpdateBit);
  };

  // TODO! Finish these two
  const setSectionCompleted = async sectionAndWeekId => {
    setSectionCompleted(newSectionProgress => {
      return {
        ...newSectionProgress,
        [sectionAndWeekId]: true,
      };
    });
    setCompletionUpdateBit(!completionUpdateBit);
  };

  const setSelfCheckCompleted = async (
    selfCheckKey: string,
    isSet: boolean = true,
  ): Promise<void> => {
    await setSelfCheckCompletedForUser({
      selfCheckProgress,
      selfCheckProgressDocRef,
      selfCheckKey,
      isSet,
      t,
    });
    setCompletionUpdateBit(!completionUpdateBit);
  };

  return (
    <CompletionContext.Provider
      value={{
        isCompletionLoading,
        isLessonCompleted,
        isLessonSlideCompleted,
        isAssnCompleted,
        isSectionCompleted,
        isSelfCheckCompleted,
        setLessonCompleted,
        setLessonSlideCompleted,
        setAssnCompleted,
        setSectionCompleted,
        setSelfCheckCompleted,
        isTeachNowTrainingCompleted,
        isTeachNowTrainingSlideCompleted,
        setTeachNowTrainingSlideCompleted,
        setTeachNowTrainingCompleted,
        numTeacherPuzzlesCompleted,
        updateTeacherPuzzlesCompleted,
        isReadAboutTeachNowCompleted,
        lessonsProgress: lessonsProgress || {},
        assnProgress: assnProgress || {},
        selfCheckProgress: selfCheckProgress || {},
        sectionProgress: sectionProgress || {},
        completionUpdateBit,
      }}
    >
      {children}
    </CompletionContext.Provider>
  );
};

export const setSelfCheckCompletedForUser = async ({
  selfCheckProgress,
  selfCheckProgressDocRef,
  selfCheckKey,
  isSet,
  t,
}: {
  /**
   * The user's current self-check state, used only to skip a write that would
   * not change anything. Omit it when the caller has no live subscription: the
   * write below is a merge and needs no prior state, so the only cost is an
   * occasional redundant write.
   */
  selfCheckProgress?: SelfCheckProgress;
  selfCheckProgressDocRef: DocumentReference<SelfCheckProgress>;
  selfCheckKey: string;
  isSet: boolean;
  t: TranslationFunction;
}) => {
  if (selfCheckProgress && selfCheckProgress[selfCheckKey] === isSet) {
    return;
  }
  try {
    await setDoc(
      selfCheckProgressDocRef,
      {
        [selfCheckKey]: isSet,
      },
      {merge: true},
    );
  } catch (e) {
    alertError(e, t);
  }
};
